import { useEffect, useRef } from 'react';
import { useApi, prefsStore, usePrefsValue, useSession } from '@adiox/ui';
import { notifications } from '../notifications/notificationStore';
import { conditionLabel, missedSince, normalizeAlerts, toServer } from './alertModel';
import { useAlerts } from './useAlerts';

const seenKey = (login: number) => `adiox.alertsSeen.${login}`;
function readSeen(login: number): string | null {
  try {
    return localStorage.getItem(seenKey(login));
  } catch {
    return null;
  }
}
function writeSeen(login: number, at: string): void {
  try {
    localStorage.setItem(seenKey(login), at);
  } catch {
    // private mode: missed alerts are simply not listed next time
  }
}

/**
 * The server watches the alerts, and an alert going off while the terminal is open arrives as a hub event (the notification
 * feed shows it). This hook does the two things left: on opening, it lists the alerts that went off while this device was away,
 * and it moves alerts an older terminal kept in the preferences to the server, once.
 */
export function useAlertWatcher(): void {
  const api = useApi();
  const readOnly = useSession().user?.readOnly === true;
  const { login, alerts, loaded } = useAlerts();
  const told = useRef<number | null>(null);

  useEffect(() => {
    if (!loaded || login === undefined || told.current === login) return;
    told.current = login;
    for (const a of missedSince(alerts, readSeen(login))) {
      notifications.getState().add({
        severity: 'warning',
        title: `Price alert: ${a.symbol} (while you were away)`,
        text: `${conditionLabel(a)} ${a.price}: the ${a.field} was ${a.triggeredValue ?? a.price}.`,
        sound: 'alert',
      });
    }
    writeSeen(login, new Date().toISOString());
  }, [loaded, login, alerts]);

  // Keep the "seen" mark current while open, so a live alert is not told again as missed next time.
  useEffect(() => {
    if (login === undefined || told.current !== login) return;
    writeSeen(login, new Date().toISOString());
  }, [alerts, login]);

  const legacy = usePrefsValue((s) => s.data.priceAlerts);
  useEffect(() => {
    if (readOnly || login === undefined || !Array.isArray(legacy) || legacy.length === 0) return;
    const moving = normalizeAlerts(legacy).filter((a) => a.active);
    prefsStore.set('priceAlerts', []);
    for (const a of moving) {
      // one that would go off at once, or an unknown symbol, is refused by the server and dropped
      void api
        .POST('/api/v1/accounts/{login}/alerts', { params: { path: { login } }, body: toServer(a) })
        .catch(() => undefined);
    }
  }, [api, legacy, login, readOnly]);
}
